import React from 'react';
import StudentCard from './StudentCard';
import { Paginator } from '../ui';

/**
 * StudentGrid — the card view of the students list.
 *
 * ⚠️ The paginator is `ui/Paginator`, the same one `AppDataTable` mounts for the
 * LIST view. It used to be ~45 lines of hand-rolled markup — Bootstrap
 * `variant="dark"` / `"light"` buttons, its own ellipsis maths, and a
 * `.pagination-info` styled from `_bootstrap-skin.scss` at a raw **14px** while
 * `Paginator`'s own info line is 13px. So switching List → Grid changed the size
 * of the "Showing 1 to 10 of 40" line and swapped the controls underneath it,
 * on one page, for no reason a reader could see.
 *
 * `getPaginationNumbers` lives in the shared component too, so there is one
 * answer to "which page numbers get an ellipsis" rather than two that agreed by
 * coincidence.
 */
const StudentGrid = ({
  students = [], onDelete, canEdit = false, page = 1, setPage, total = 0, pageSize = 10,
  /* Defaults false, mirroring `StudentList` — a `flex-1 min-h-0` scroll box in
     an unbounded page is a subtle thing to inherit by accident, and the prop
     keeps the two views' contracts identical. */
  fill = false,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className={fill ? "tw-flex tw-min-h-0 tw-flex-1 tw-flex-col" : undefined}>
      {/* ⚠️ A DEDICATED scroll box, not `overflow` on `.row`. Bootstrap's row
          carries negative gutter margins, and a scroll container wrapped
          straight around them produces a horizontal scrollbar with nothing to
          scroll to. The 1px of padding is so a card's hover shadow is not
          clipped at the edges. */}
      <div className={fill
        ? "tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-overscroll-contain tw-p-1"
        : undefined}>
        <div className="row g-4">
          {students.map(student => (
            <div key={student._id} className="col-12 col-md-6 col-lg-4">
              <StudentCard student={student} onDelete={onDelete} canEdit={canEdit} />
            </div>
          ))}
        </div>
      </div>

      {/* `showSinglePage`, as `AppDataTable` passes for the LIST view — so the
          "Showing 1 to 8 of 8" line is present in both views at one page, and
          the two views agree. It is also what the deleted `sl-footer` was for:
          that block existed ONLY for `total <= pageSize`, which is exactly the
          case a paginator hides itself. */}
      <Paginator
        showSinglePage
        className={fill ? "tw-shrink-0 tw-pt-3" : "mt-3"}
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onChange={setPage}
        infoLabel="students"
      />
    </div>
  );
};

export default StudentGrid;
